import React, { useState, useEffect, useMemo } from 'react';

const formatWithCommas = (val) => {
  if (!val && val !== 0) return '';
  const num = String(val).replace(/,/g, '');
  if (num === '' || num === '-') return num;
  const pts = num.split('.');
  pts[0] = pts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return pts.join('.');
};

const formatMoney = (val) => `$${Math.round(val).toLocaleString()}`;

const calculateIRR = (cashFlows) => {
  if (cashFlows.length < 2) return 0;
  const totalOutflow = cashFlows.slice(0, -1).reduce((sum, cf) => sum + Math.abs(cf), 0);
  if (totalOutflow === 0) return 0;
  
  const npv = (rate) => cashFlows.reduce((sum, cf, i) => sum + cf / Math.pow(1 + rate, i), 0);
  let low = -0.99, high = 2;
  
  if (npv(low) * npv(high) > 0) {
    high = 10;
    if (npv(low) * npv(high) > 0) return npv(0) > 0 ? high : low;
  }
  
  for (let i = 0; i < 100; i++) {
    const mid = (low + high) / 2;
    if (Math.abs(npv(mid)) < 0.01) return mid;
    if (npv(low) * npv(mid) < 0) high = mid;
    else low = mid;
  }
  return (low + high) / 2;
};

const LuxuryInput = ({ label, value, onChange, prefix, suffix, disabled, dark }) => {
  const [display, setDisplay] = useState(formatWithCommas(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDisplay(formatWithCommas(value));
  }, [value, focused]);

  const handleChange = (e) => {
    if (disabled) return;
    const raw = e.target.value.replace(/,/g, '');
    if (raw === '' || raw === '-' || /^-?\d*\.?\d*$/.test(raw)) {
      setDisplay(e.target.value);
      onChange(raw);
    }
  };

  return (
    <div className="group">
      <label className={`block text-xs font-semibold uppercase tracking-wider mb-1 ${dark ? 'text-blue-300/70' : 'text-slate-500'}`}>{label}</label>
      <div className="relative">
        {prefix && <span className={`absolute left-2.5 top-1/2 -translate-y-1/2 text-xs ${dark ? 'text-amber-400/60' : 'text-blue-600/60'}`}>{prefix}</span>}
        <input
          type="text"
          inputMode="decimal"
          value={display}
          onChange={handleChange}
          onFocus={(e) => { if (!disabled) { setFocused(true); e.target.select(); } }}
          onBlur={() => { setFocused(false); setDisplay(formatWithCommas(value)); }}
          disabled={disabled}
          className={`w-full h-8 text-sm border rounded-lg text-right font-medium transition-all duration-300 focus:outline-none ${dark ? 'bg-slate-900/50 text-white border-blue-500/20 focus:border-amber-400/50 focus:ring-1 focus:ring-amber-400/20' : 'bg-white text-slate-800 border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 shadow-sm'} ${prefix ? 'pl-6' : 'pl-2'} ${suffix ? 'pr-8' : 'pr-2'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        />
        {suffix && <span className={`absolute right-2.5 top-1/2 -translate-y-1/2 text-xs ${dark ? 'text-amber-400/60' : 'text-blue-600/60'}`}>{suffix}</span>}
      </div>
    </div>
  );
};

const Toggle = ({ label, checked, onChange, dark }) => (
  <div className="flex items-center justify-between py-2">
    <span className={`text-sm ${dark ? 'text-blue-200/80' : 'text-slate-600'}`}>{label}</span>
    <button
      onClick={() => onChange(!checked)}
      className={`relative w-12 h-6 rounded-full transition-all duration-300 ${checked ? (dark ? 'bg-gradient-to-r from-amber-500 to-amber-400' : 'bg-gradient-to-r from-blue-500 to-blue-400') : (dark ? 'bg-slate-700/50 border border-blue-500/20' : 'bg-slate-200 border border-slate-300')}`}
    >
      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-lg transition-all duration-300 ${checked ? 'left-7' : 'left-1'}`} />
    </button>
  </div>
);

const StatCard = ({ label, value, colorClass, icon, dark }) => (
  <div className={`relative overflow-hidden rounded-2xl p-5 ${dark ? 'bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-blue-500/10' : 'bg-white border border-slate-200 shadow-lg'} backdrop-blur-xl group ${dark ? 'hover:border-amber-400/30' : 'hover:border-blue-400/50'} transition-all duration-500`}>
    <div className={`absolute inset-0 ${dark ? 'bg-gradient-to-br from-blue-600/5 to-transparent' : 'bg-gradient-to-br from-blue-50/50 to-transparent'} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
    <div className="relative">
      <div className="flex items-center gap-2 mb-2">
        <span className={`text-lg ${colorClass}`}>{icon}</span>
        <span className={`text-xs font-semibold uppercase tracking-wider ${dark ? 'text-blue-300/60' : 'text-slate-400'}`}>{label}</span>
      </div>
      <p className={`text-2xl font-bold ${colorClass}`}>{value}</p>
    </div>
  </div>
);

const CostItem = ({ label, value, dark }) => (
  <div className="flex items-center justify-between group">
    <div className="flex items-center gap-3">
      <div className={`w-2 h-2 rounded-full bg-gradient-to-r from-rose-400 to-rose-500 ${dark ? 'group-hover:shadow-lg group-hover:shadow-rose-500/30' : ''} transition-all duration-300`} />
      <span className={`${dark ? 'text-blue-200/70 group-hover:text-blue-100' : 'text-slate-600 group-hover:text-slate-800'} transition-colors`}>{label}</span>
    </div>
    <span className={`font-bold ${dark ? 'text-white' : 'text-slate-800'}`}>{formatMoney(value)}</span>
  </div>
);

export default function WealthCalculator() {
  const [darkMode, setDarkMode] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [taxMethod, setTaxMethod] = useState('gross');
  const [paramsOpen, setParamsOpen] = useState(false);
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [bulkPremium, setBulkPremium] = useState('');
  const [bulkCashValues, setBulkCashValues] = useState('');
  const [bulkDeathBenefits, setBulkDeathBenefits] = useState('');
  const [policyData, setPolicyData] = useState({ 
    basePremium: 0,
    baseCashValues: [],
    baseDeathBenefits: [],
  });

  const [inputs, setInputs] = useState({
    capitalInput: '0', existingCapital: '0', timePeriod: '25', taxes: '0',
    compoundingRate: '0', termCosts: '0', opportunityCost: '0', managementFees: '0', planningFees: '0',
    annualPremium: '0', futureCashValue: '0', futureDeathBenefit: '0', inflation: '0'
  });

  const handleChange = (field, value) => {
    setInputs(prev => {
      const newInputs = { ...prev, [field]: value };
      if (field === 'capitalInput') {
        newInputs.annualPremium = value;
      }
      return newInputs;
    });
  };

  const scalingFactor = useMemo(() => {
    if (policyData.basePremium <= 0) return 1;
    const currentPremium = parseFloat(inputs.annualPremium) || 0;
    return currentPremium / policyData.basePremium;
  }, [inputs.annualPremium, policyData.basePremium]);

  const scaledPolicyData = useMemo(() => ({
    cashValues: policyData.baseCashValues.map(v => v * scalingFactor),
    deathBenefits: policyData.baseDeathBenefits.map(v => v * scalingFactor),
  }), [policyData.baseCashValues, policyData.baseDeathBenefits, scalingFactor]);

  useEffect(() => {
    if (scaledPolicyData.cashValues.length > 0) {
      const years = parseInt(inputs.timePeriod) || 0;
      if (years > 0) {
        const cvIndex = Math.min(years - 1, scaledPolicyData.cashValues.length - 1);
        const dbIndex = Math.min(years - 1, scaledPolicyData.deathBenefits.length - 1);
        setInputs(prev => ({
          ...prev,
          futureCashValue: Math.round(scaledPolicyData.cashValues[cvIndex] || 0).toString(),
          futureDeathBenefit: Math.round(scaledPolicyData.deathBenefits[dbIndex] || 0).toString()
        }));
      }
    }
  }, [inputs.timePeriod, scaledPolicyData]);

  const applyPolicyData = () => {
    const premium = parseFloat(bulkPremium.replace(/,/g, '')) || 0;
    const cashValues = bulkCashValues.split('\n').map(v => parseFloat(v.replace(/,/g, '').trim()) || 0).filter(v => v > 0);
    const deathBenefits = bulkDeathBenefits.split('\n').map(v => parseFloat(v.replace(/,/g, '').trim()) || 0).filter(v => v > 0);
    
    setPolicyData({ basePremium: premium, baseCashValues: cashValues, baseDeathBenefits: deathBenefits });
    
    const years = parseInt(inputs.timePeriod) || 0;
    if (years > 0 && cashValues.length > 0) {
      const cvIndex = Math.min(years - 1, cashValues.length - 1);
      const dbIndex = Math.min(years - 1, deathBenefits.length - 1);
      setInputs(prev => ({
        ...prev,
        annualPremium: premium.toString(),
        futureCashValue: Math.round(cashValues[cvIndex] || 0).toString(),
        futureDeathBenefit: Math.round(deathBenefits[dbIndex] || 0).toString()
      }));
    }
    setPolicyModalOpen(false);
  };

  const yearlyData = useMemo(() => {
    const annual = parseFloat(inputs.capitalInput) || 0;
    const current = parseFloat(inputs.existingCapital) || 0;
    const rate = (parseFloat(inputs.compoundingRate) || 0) / 100;
    const time = parseInt(inputs.timePeriod) || 0;
    const taxRate = (parseFloat(inputs.taxes) || 0) / 100;
    const mgmtRate = (parseFloat(inputs.managementFees) || 0) / 100;
    const term = parseFloat(inputs.termCosts) || 0;
    const plan = parseFloat(inputs.planningFees) || 0;
    const opp = (parseFloat(inputs.opportunityCost) || 0) / 100;
    const inflationRate = (parseFloat(inputs.inflation) || 0) / 100;

    const rows = [];
    let grossVal = current;
    let accountVal = current;
    let cumContributions = current;
    let cumTax = 0;
    let cumMgmt = 0;
    let deferredTaxWithLOC = 0;

    for (let y = 1; y <= time; y++) {
      grossVal += annual;
      accountVal += annual;
      cumContributions += annual;

      const grossGain = grossVal * rate;
      grossVal += grossGain;
      
      const accountGain = accountVal * rate;
      accountVal += accountGain;
      
      const yearTax = accountGain * taxRate;
      
      if (taxMethod === 'net') {
        accountVal -= yearTax;
        cumTax += yearTax;
      } else {
        deferredTaxWithLOC = deferredTaxWithLOC * (1 + opp) + yearTax;
        cumTax = deferredTaxWithLOC;
      }

      const mgmtFee = accountVal * mgmtRate;
      accountVal -= mgmtFee;
      cumMgmt += mgmtFee;

      let termTotal = 0;
      let planTotal = 0;
      if (opp > 0) {
        if (term > 0) termTotal = term * ((Math.pow(1 + opp, y) - 1) / opp);
        if (plan > 0) planTotal = plan * ((Math.pow(1 + opp, y) - 1) / opp);
      } else {
        termTotal = term * y;
        planTotal = plan * y;
      }

      const taxLiability = taxMethod === 'gross' ? cumTax : 0;
      const preInflationNet = accountVal - taxLiability - termTotal - planTotal;
      
      const inflationFactor = Math.pow(1 + inflationRate, y);
      const netVal = preInflationNet / inflationFactor;
      const inflationCost = preInflationNet - netVal;

      rows.push({
        year: y,
        contribution: annual,
        cumContributions,
        grossAssetValue: grossVal,
        yearGain: accountGain,
        yearTax,
        cumTax,
        mgmtFee,
        cumMgmt,
        termTotal,
        planTotal,
        inflationCost,
        accountValue: accountVal,
        netValue: netVal
      });
    }
    return rows;
  }, [inputs, taxMethod]);

  const lastYear = yearlyData[yearlyData.length - 1] || null;

  const pli = useMemo(() => {
    const years = parseInt(inputs.timePeriod) || 0;
    const premium = parseFloat(inputs.annualPremium) || 0;
    const cashValue = parseFloat(inputs.futureCashValue) || 0;
    const deathBenefit = parseFloat(inputs.futureDeathBenefit) || 0;

    if (years === 0 || premium === 0) return { cvIRR: 0, dbIRR: 0, totalPremiums: 0 };

    const cvFlows = Array(years).fill(-premium);
    cvFlows.push(cashValue);
    const dbFlows = Array(years).fill(-premium);
    dbFlows.push(deathBenefit);

    return { cvIRR: calculateIRR(cvFlows) * 100, dbIRR: calculateIRR(dbFlows) * 100, totalPremiums: premium * years };
  }, [inputs]);

  const { assetIRR, externalIRR } = useMemo(() => {
    const years = parseInt(inputs.timePeriod) || 0;
    const annual = parseFloat(inputs.capitalInput) || 0;
    const current = parseFloat(inputs.existingCapital) || 0;
    
    if (years === 0 || !lastYear) return { assetIRR: 0, externalIRR: 0 };
    if (annual === 0 && current === 0) return { assetIRR: 0, externalIRR: 0 };

    const baseCashFlows = [-current - annual];
    for (let i = 1; i < years; i++) baseCashFlows.push(-annual);
    
    const assetFlows = [...baseCashFlows, lastYear.grossAssetValue];
    const extFlows = [...baseCashFlows, lastYear.netValue];

    return { 
      assetIRR: calculateIRR(assetFlows) * 100, 
      externalIRR: calculateIRR(extFlows) * 100 
    };
  }, [inputs, lastYear]);

  const hasCosts = parseFloat(inputs.taxes) > 0 || parseFloat(inputs.managementFees) > 0 || 
                   parseFloat(inputs.termCosts) > 0 || parseFloat(inputs.planningFees) > 0 || 
                   parseFloat(inputs.inflation) > 0;

  const CompareSection = () => {
    const [includeMgmtFees, setIncludeMgmtFees] = useState(false);
    const [includeExternalCosts, setIncludeExternalCosts] = useState(false);
    const [includeRecoveredCosts, setIncludeRecoveredCosts] = useState(false);

    const cashValue = parseFloat(inputs.futureCashValue) || 0;
    const deathBenefit = parseFloat(inputs.futureDeathBenefit) || 0;
    const netDeathBenefit = deathBenefit - cashValue;
    const annualPremium = parseFloat(inputs.annualPremium) || 0;
    const years = parseInt(inputs.timePeriod) || 0;
    const inflation = (parseFloat(inputs.inflation) || 0) / 100;
    const annual = parseFloat(inputs.capitalInput) || 0;
    const current = parseFloat(inputs.existingCapital) || 0;

    const tradCalc = useMemo(() => {
      if (!lastYear || years === 0) return { netWealth: 0, irr: 0 };

      let netWealth;

      if (includeMgmtFees) {
        netWealth = lastYear.accountValue;
        
        if (includeExternalCosts) {
          if (taxMethod === 'gross') {
            netWealth -= lastYear.cumTax;
          }
          netWealth -= lastYear.termTotal;
          netWealth -= lastYear.planTotal;
          if (inflation > 0) netWealth = netWealth / Math.pow(1 + inflation, years);
        }
      } else {
        netWealth = lastYear.grossAssetValue;
        
        if (includeExternalCosts) {
          netWealth -= lastYear.cumTax;
          netWealth -= lastYear.termTotal;
          netWealth -= lastYear.planTotal;
          if (inflation > 0) netWealth = netWealth / Math.pow(1 + inflation, years);
        }
      }

      const cashFlows = [-current - annual];
      for (let i = 1; i < years; i++) cashFlows.push(-annual);
      cashFlows.push(netWealth);

      return { netWealth, irr: calculateIRR(cashFlows) * 100 };
    }, [lastYear, years, includeMgmtFees, includeExternalCosts, annual, current, inflation]);

    const pliCalc = useMemo(() => {
      if (years === 0) return { totalWealth: 0, irr: 0 };

      let totalWealth = cashValue + netDeathBenefit;
      
      if (includeRecoveredCosts && lastYear) {
        totalWealth += lastYear.cumTax + lastYear.termTotal + lastYear.planTotal;
      }

      if (inflation > 0) totalWealth = totalWealth / Math.pow(1 + inflation, years);

      const cashFlows = [];
      for (let i = 0; i < years; i++) cashFlows.push(-annualPremium);
      cashFlows.push(totalWealth);

      return { totalWealth, irr: annualPremium > 0 ? calculateIRR(cashFlows) * 100 : 0 };
    }, [cashValue, netDeathBenefit, years, annualPremium, includeRecoveredCosts, lastYear, inflation]);

    const wealthAdvantage = pliCalc.totalWealth - tradCalc.netWealth;
    const advantagePercent = tradCalc.netWealth > 0 ? (wealthAdvantage / tradCalc.netWealth) * 100 : 0;
    const pliWins = wealthAdvantage > 0;
    
    const hasCostsApplied = includeMgmtFees || includeExternalCosts;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div className={`rounded-2xl overflow-hidden flex flex-col ${darkMode ? 'bg-gradient-to-br from-slate-800/90 to-slate-900/90 border border-blue-500/20' : 'bg-white border border-slate-200 shadow-xl'} backdrop-blur-xl`}>
            <div className={`p-5 border-b ${darkMode ? 'border-blue-500/10' : 'border-slate-100'}`}>
              <div className="flex items-center gap-2">
                <span className={`text-lg ${darkMode ? 'text-amber-400' : 'text-blue-600'}`}>◈</span>
                <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-800'}`}>Current Asset</h3>
              </div>
            </div>
            
            <div className="p-5 space-y-3 flex-1">
              <div className="flex items-center justify-between">
                <span className={`text-sm ${darkMode ? 'text-blue-300/70' : 'text-slate-500'}`}>Gross Asset Value</span>
                <span className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{formatMoney(lastYear?.grossAssetValue || 0)}</span>
              </div>
              
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setIncludeMgmtFees(!includeMgmtFees)}
                  className={`flex-1 py-2 px-3 rounded-xl text-xs font-semibold transition-all duration-300 ${includeMgmtFees ? (darkMode ? 'bg-gradient-to-r from-amber-500 to-amber-400 text-slate-900' : 'bg-gradient-to-r from-blue-500 to-blue-400 text-white') : (darkMode ? 'bg-slate-700/50 text-blue-300/70 hover:bg-slate-600/50 border border-blue-500/20' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200')}`}
                >
                  Mgmt Fees
                </button>
                <button
                  onClick={() => setIncludeExternalCosts(!includeExternalCosts)}
                  className={`flex-1 py-2 px-3 rounded-xl text-xs font-semibold transition-all duration-300 ${includeExternalCosts ? (darkMode ? 'bg-gradient-to-r from-amber-500 to-amber-400 text-slate-900' : 'bg-gradient-to-r from-blue-500 to-blue-400 text-white') : (darkMode ? 'bg-slate-700/50 text-blue-300/70 hover:bg-slate-600/50 border border-blue-500/20' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200')}`}
                >
                  External Costs
                </button>
              </div>
            </div>
            
            <div className={`p-6 text-center ${darkMode ? 'bg-gradient-to-t from-blue-950/50 to-transparent' : 'bg-gradient-to-t from-slate-50 to-transparent'}`}>
              <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-blue-400/60' : 'text-slate-400'}`}>Net Wealth</p>
              <p className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{formatMoney(tradCalc.netWealth)}</p>
              <p className={`font-semibold mt-1 ${tradCalc.irr < 0 ? 'text-rose-400' : (darkMode ? 'text-amber-400' : 'text-blue-600')}`}>
                {tradCalc.irr.toFixed(2)}% {hasCostsApplied ? 'ExRR' : 'IRR'}
              </p>
            </div>
          </div>

          <div className={`rounded-2xl overflow-hidden flex flex-col ${darkMode ? 'bg-gradient-to-br from-slate-800/90 to-slate-900/90 border border-blue-500/20' : 'bg-white border border-slate-200 shadow-xl'} backdrop-blur-xl`}>
            <div className={`p-5 border-b ${darkMode ? 'border-blue-500/10' : 'border-slate-100'}`}>
              <div className="flex items-center gap-2">
                <span className="text-emerald-400 text-lg">◆</span>
                <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-800'}`}>PLI Strategy</h3>
              </div>
            </div>
            
            <div className="p-5 space-y-3 flex-1">
              <div className="flex items-center justify-between">
                <span className={`text-sm ${darkMode ? 'text-blue-300/70' : 'text-slate-500'}`}>Cash Value</span>
                <span className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{formatMoney(cashValue)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className={`text-sm ${darkMode ? 'text-blue-300/70' : 'text-slate-500'}`}>Net Death Benefit</span>
                <span className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{formatMoney(netDeathBenefit)}</span>
              </div>
              <Toggle label="Include Recovered Costs" checked={includeRecoveredCosts} onChange={setIncludeRecoveredCosts} dark={darkMode} />
            </div>
            
            <div className={`p-6 text-center ${darkMode ? 'bg-gradient-to-t from-emerald-950/30 to-transparent' : 'bg-gradient-to-t from-emerald-50 to-transparent'}`}>
              <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-emerald-400/60' : 'text-emerald-600/60'}`}>Total Wealth</p>
              <p className="text-4xl font-bold text-emerald-500">{formatMoney(pliCalc.totalWealth)}</p>
              <p className={`font-semibold mt-1 ${pliCalc.irr < 0 ? 'text-rose-400' : 'text-emerald-500'}`}>
                {pliCalc.irr.toFixed(2)}% {includeRecoveredCosts ? 'ExRR' : 'IRR'}
              </p>
            </div>
          </div>
        </div>

        <div className={`rounded-2xl p-8 text-center ${darkMode ? 'bg-gradient-to-r from-blue-950/50 via-slate-900/80 to-blue-950/50 border border-amber-500/20' : 'bg-gradient-to-r from-blue-50 via-white to-blue-50 border border-blue-200 shadow-lg'} backdrop-blur-xl`}>
          <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-amber-400/70' : 'text-blue-600/70'}`}>Wealth Advantage</p>
          <p className={`text-5xl font-bold ${pliWins ? 'text-emerald-500' : 'text-rose-500'}`}>
            {pliWins ? '+' : ''}{formatMoney(wealthAdvantage)}
          </p>
          <p className={`mt-2 ${darkMode ? 'text-blue-300/60' : 'text-slate-500'}`}>
            {Math.abs(advantagePercent).toFixed(1)}% {pliWins ? 'additional' : 'less'} wealth at Year {inputs.timePeriod}
          </p>
        </div>
      </div>
    );
  };

  const navItems = [
    { id: 'overview', label: 'Overview', icon: '◎' },
    { id: 'projections', label: 'Cost Analysis', icon: '◈' },
    { id: 'insurance', label: 'PLI Values', icon: '◇' },
    { id: 'compare', label: 'Compare', icon: '⬡' },
    { id: 'details', label: 'Projections', icon: '▤' }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-500 ${darkMode ? 'bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950' : 'bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50'}`}>
      {/* Ambient glow effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-0 left-1/4 w-96 h-96 ${darkMode ? 'bg-blue-500/10' : 'bg-blue-400/20'} rounded-full blur-3xl`} />
        <div className={`absolute bottom-0 right-1/4 w-96 h-96 ${darkMode ? 'bg-amber-500/5' : 'bg-amber-400/10'} rounded-full blur-3xl`} />
      </div>

      {/* Parameters Drawer */}
      <div className={`fixed left-0 top-0 h-full w-72 z-50 transform transition-transform duration-500 ease-out ${paramsOpen ? 'translate-x-0' : '-translate-x-full'} ${darkMode ? 'bg-gradient-to-b from-slate-900/98 to-slate-950/98 border-r border-blue-500/20' : 'bg-gradient-to-b from-white to-slate-50 border-r border-slate-200'} backdrop-blur-xl shadow-2xl`}>
        <div className="p-4 h-screen overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-sm font-bold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              <span className={`w-8 h-8 rounded-lg ${darkMode ? 'bg-gradient-to-br from-amber-500 to-amber-600 text-slate-900' : 'bg-gradient-to-br from-blue-500 to-blue-600 text-white'} flex items-center justify-center text-xs shadow-lg`}>⚙</span>
              Parameters
            </h3>
            <button onClick={() => setParamsOpen(false)} className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-300 ${darkMode ? 'hover:bg-slate-800 text-blue-300/70 hover:text-white' : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'}`}>✕</button>
          </div>
          
          <div className="space-y-3">
            <LuxuryInput label="Time Horizon" value={inputs.timePeriod} onChange={(v) => handleChange('timePeriod', v)} suffix="years" dark={darkMode} />
            <LuxuryInput label="Annual Contribution" value={inputs.capitalInput} onChange={(v) => handleChange('capitalInput', v)} prefix="$" dark={darkMode} />
            <LuxuryInput label="Current Assets" value={inputs.existingCapital} onChange={(v) => handleChange('existingCapital', v)} prefix="$" dark={darkMode} />
            <LuxuryInput label="Growth Rate" value={inputs.compoundingRate} onChange={(v) => handleChange('compoundingRate', v)} suffix="%" dark={darkMode} />

            <div className={`pt-3 mt-3 border-t ${darkMode ? 'border-blue-500/10' : 'border-slate-200'}`}>
              <label className={`block text-xs font-semibold uppercase tracking-wider mb-3 ${darkMode ? 'text-amber-400/80' : 'text-blue-600'}`}>Cost Analysis</label>
              <div className="space-y-3">
                <LuxuryInput label="Management Fees" value={inputs.managementFees} onChange={(v) => handleChange('managementFees', v)} suffix="%" dark={darkMode} />
                <LuxuryInput label="Tax Rate" value={inputs.taxes} onChange={(v) => handleChange('taxes', v)} suffix="%" dark={darkMode} />
                
                <div>
                  <label className={`block text-xs font-semibold uppercase tracking-wider mb-1.5 ${darkMode ? 'text-blue-300/70' : 'text-slate-500'}`}>Tax Method</label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {['net', 'gross'].map((method) => (
                      <button key={method} onClick={() => setTaxMethod(method)}
                        className={`py-1.5 rounded-lg text-xs font-semibold transition-all duration-300 ${taxMethod === method ? (darkMode ? 'bg-gradient-to-r from-amber-500 to-amber-400 text-slate-900 shadow-lg shadow-amber-500/30' : 'bg-gradient-to-r from-blue-500 to-blue-400 text-white shadow-lg shadow-blue-500/30') : (darkMode ? 'bg-slate-800/50 text-blue-300/70 hover:bg-slate-700/50 border border-blue-500/20' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200')}`}>
                        {method.charAt(0).toUpperCase() + method.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                <LuxuryInput label="Term Insurance" value={inputs.termCosts} onChange={(v) => handleChange('termCosts', v)} prefix="$" dark={darkMode} />
                <LuxuryInput label="Other Annual Cost" value={inputs.planningFees} onChange={(v) => handleChange('planningFees', v)} prefix="$" dark={darkMode} />
                <LuxuryInput label="Opportunity Cost" value={inputs.opportunityCost} onChange={(v) => handleChange('opportunityCost', v)} suffix="%" dark={darkMode} />
                <LuxuryInput label="Inflation" value={inputs.inflation} onChange={(v) => handleChange('inflation', v)} suffix="%" dark={darkMode} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Policy Modal */}
      {policyModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className={`absolute inset-0 ${darkMode ? 'bg-slate-950/80' : 'bg-slate-900/50'} backdrop-blur-sm`} onClick={() => setPolicyModalOpen(false)} />
          <div className={`relative w-full max-w-2xl mx-4 rounded-3xl shadow-2xl ${darkMode ? 'bg-gradient-to-br from-slate-900 to-slate-950 border border-blue-500/20' : 'bg-white border border-slate-200'}`}>
            <div className="p-8">
              <div className="flex items-center justify-between mb-8">
                <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>Policy Data Entry</h3>
                <button onClick={() => setPolicyModalOpen(false)} className={`w-8 h-8 rounded-xl flex items-center justify-center ${darkMode ? 'hover:bg-slate-800 text-blue-300/70 hover:text-white' : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'} transition-colors`}>✕</button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <label className={`block text-xs font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-amber-400' : 'text-blue-600'}`}>Base Annual Premium (for scaling)</label>
                  <div className="relative">
                    <span className={`absolute left-4 top-1/2 -translate-y-1/2 text-sm ${darkMode ? 'text-amber-400/60' : 'text-blue-600/60'}`}>$</span>
                    <input type="text" value={bulkPremium} onChange={(e) => setBulkPremium(e.target.value)}
                      className={`w-full h-12 pl-8 pr-4 rounded-xl border text-right ${darkMode ? 'bg-slate-800/50 text-white border-blue-500/20 focus:border-amber-400/50' : 'bg-slate-50 text-slate-800 border-slate-200 focus:border-blue-500'} focus:outline-none focus:ring-2 ${darkMode ? 'focus:ring-amber-400/20' : 'focus:ring-blue-500/20'}`} placeholder="0" />
                  </div>
                  <p className={`text-xs mt-2 ${darkMode ? 'text-blue-300/50' : 'text-slate-400'}`}>Enter the premium these values are based on. You can scale later by changing the Annual Premium.</p>
                </div>
                
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className={`block text-xs font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-amber-400' : 'text-blue-600'}`}>Cash Values (One Per Line)</label>
                    <textarea value={bulkCashValues} onChange={(e) => setBulkCashValues(e.target.value)}
                      className={`w-full h-48 p-4 rounded-xl border resize-none ${darkMode ? 'bg-slate-800/50 text-white border-blue-500/20 focus:border-amber-400/50' : 'bg-slate-50 text-slate-800 border-slate-200 focus:border-blue-500'} focus:outline-none focus:ring-2 ${darkMode ? 'focus:ring-amber-400/20' : 'focus:ring-blue-500/20'}`}
                      placeholder="15000&#10;32000&#10;51000" />
                  </div>
                  <div>
                    <label className={`block text-xs font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-amber-400' : 'text-blue-600'}`}>Death Benefits (One Per Line)</label>
                    <textarea value={bulkDeathBenefits} onChange={(e) => setBulkDeathBenefits(e.target.value)}
                      className={`w-full h-48 p-4 rounded-xl border resize-none ${darkMode ? 'bg-slate-800/50 text-white border-blue-500/20 focus:border-amber-400/50' : 'bg-slate-50 text-slate-800 border-slate-200 focus:border-blue-500'} focus:outline-none focus:ring-2 ${darkMode ? 'focus:ring-amber-400/20' : 'focus:ring-blue-500/20'}`}
                      placeholder="500000&#10;500000&#10;500000" />
                  </div>
                </div>
                
                <button onClick={applyPolicyData} className={`w-full py-4 rounded-xl font-semibold ${darkMode ? 'text-slate-900 bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 shadow-lg shadow-amber-500/30' : 'text-white bg-gradient-to-r from-blue-500 to-blue-400 hover:from-blue-400 hover:to-blue-300 shadow-lg shadow-blue-500/30'} transition-all duration-300`}>
                  Apply Data
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={`relative z-10 transition-all duration-500 ${paramsOpen ? 'ml-72' : 'ml-0'}`}>
        {/* Header */}
        <header className={`relative border-b ${darkMode ? 'border-blue-500/10 bg-slate-950/50' : 'border-slate-200 bg-white/80'} backdrop-blur-xl`}>
          <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl ${darkMode ? 'bg-gradient-to-br from-blue-600 to-blue-700 shadow-lg shadow-blue-500/30' : 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg shadow-blue-500/20'} flex items-center justify-center`}>
                <span className="text-white font-bold text-lg">IR</span>
              </div>
              <div>
                <h1 className={`text-xl ${darkMode ? 'text-white' : 'text-slate-800'}`}>Internal & External Rates of Return</h1>
                <p className={`text-xs ${darkMode ? 'text-blue-400/50' : 'text-slate-400'}`}>Sapphire Luxe Edition</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <button onClick={() => setParamsOpen(true)} className={`px-5 py-2.5 rounded-xl font-semibold text-sm flex items-center gap-2 ${darkMode ? 'bg-slate-800/50 text-blue-300/80 hover:bg-slate-700/50 border border-blue-500/20 hover:border-amber-400/30' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200 hover:border-blue-400/50 shadow-sm'} transition-all duration-300`}>
                ⚙ Parameters
              </button>
              <button onClick={() => setDarkMode(!darkMode)} className={`relative w-14 h-7 rounded-full transition-all duration-300 ${darkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                <div className={`absolute top-1 w-5 h-5 rounded-full transition-all duration-300 flex items-center justify-center ${darkMode ? 'left-8 bg-slate-900' : 'left-1 bg-white shadow-md'}`}>
                  <span className="text-xs">{darkMode ? '🌙' : '☀️'}</span>
                </div>
              </button>
            </div>
          </div>
        </header>

        <div className="relative max-w-7xl mx-auto px-8 py-10">
          {/* Navigation */}
          <div className="flex justify-center mb-12">
            <div className={`inline-flex p-1.5 rounded-2xl ${darkMode ? 'bg-slate-900/80 border border-blue-500/20' : 'bg-white border border-slate-200 shadow-lg'} backdrop-blur-xl`}>
              {navItems.map((item) => (
                <button key={item.id} onClick={() => setActiveSection(item.id)}
                  className={`px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-300 flex items-center gap-2 ${activeSection === item.id ? (darkMode ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/30' : 'bg-gradient-to-r from-blue-500 to-blue-400 text-white shadow-lg shadow-blue-500/20') : (darkMode ? 'text-blue-300/60 hover:text-white hover:bg-slate-800/50' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50')}`}>
                  <span className="text-lg">{item.icon}</span>{item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Overview */}
          {activeSection === 'overview' && (
            <div className="grid grid-cols-2 gap-8">
              <div className="space-y-4">
                <StatCard label="Gross Asset Value" value={formatMoney(lastYear?.grossAssetValue || 0)} colorClass={darkMode ? 'text-amber-400' : 'text-blue-600'} icon="◈" dark={darkMode} />
                <StatCard label="Net After Costs" value={formatMoney(lastYear?.netValue || 0)} colorClass="text-blue-400" icon="◇" dark={darkMode} />
                <StatCard label="Internal Rate of Return" value={`${assetIRR.toFixed(2)}%`} colorClass="text-emerald-400" icon="○" dark={darkMode} />
                <StatCard label={parseFloat(inputs.inflation) > 0 ? "External Rate of Return (Real)" : "External Rate of Return"} value={`${externalIRR.toFixed(2)}%`} colorClass={externalIRR < 0 ? 'text-rose-400' : hasCosts ? 'text-teal-400' : 'text-rose-400'} icon="◎" dark={darkMode} />
              </div>

              <div className={`rounded-3xl p-6 h-full flex flex-col justify-between ${darkMode ? 'bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-blue-500/10' : 'bg-white border border-slate-200 shadow-xl'} backdrop-blur-xl`}>
                <div className="text-center">
                  <span className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-blue-400/50' : 'text-slate-400'}`}>Total Wealth</span>
                  <p className={`text-3xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-slate-800'}`}>{formatMoney(lastYear?.grossAssetValue || 0)}</p>
                </div>
                {lastYear && (() => {
                  const gains = lastYear.grossAssetValue - lastYear.cumContributions;
                  const totalCosts = lastYear.cumTax + lastYear.cumMgmt + lastYear.termTotal + lastYear.planTotal;
                  const total = lastYear.cumContributions + gains + totalCosts;
                  const principalPct = total > 0 ? (lastYear.cumContributions / total) * 100 : 0;
                  const gainsPct = total > 0 ? (gains / total) * 100 : 0;
                  const costsPct = total > 0 ? (totalCosts / total) * 100 : 0;
                  return (
                    <>
                      <div className={`p-3 rounded-xl ${darkMode ? 'bg-gradient-to-r from-blue-500/10 to-blue-600/5 border border-blue-500/20' : 'bg-gradient-to-r from-blue-50 to-blue-100/50 border border-blue-200'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-blue-400 to-blue-500 shadow-lg shadow-blue-500/50" />
                            <span className={`text-xs font-medium ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>Principal</span>
                          </div>
                          <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{formatMoney(lastYear.cumContributions)}</span>
                        </div>
                        <div className={`h-1.5 rounded-full ${darkMode ? 'bg-slate-700/50' : 'bg-blue-100'}`}>
                          <div className="h-full rounded-full bg-gradient-to-r from-blue-400 to-blue-500" style={{ width: `${principalPct}%` }} />
                        </div>
                        <p className={`text-xs mt-1 text-right ${darkMode ? 'text-blue-400/60' : 'text-blue-600/60'}`}>{principalPct.toFixed(1)}%</p>
                      </div>
                      
                      <div className={`p-3 rounded-xl ${darkMode ? 'bg-gradient-to-r from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20' : 'bg-gradient-to-r from-emerald-50 to-emerald-100/50 border border-emerald-200'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 shadow-lg shadow-emerald-500/50" />
                            <span className={`text-xs font-medium ${darkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>Investment Gains</span>
                          </div>
                          <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{formatMoney(gains)}</span>
                        </div>
                        <div className={`h-1.5 rounded-full ${darkMode ? 'bg-slate-700/50' : 'bg-emerald-100'}`}>
                          <div className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500" style={{ width: `${gainsPct}%` }} />
                        </div>
                        <p className={`text-xs mt-1 text-right ${darkMode ? 'text-emerald-400/60' : 'text-emerald-600/60'}`}>{gainsPct.toFixed(1)}%</p>
                      </div>
                      
                      <div className={`p-3 rounded-xl ${darkMode ? 'bg-gradient-to-r from-rose-500/10 to-rose-600/5 border border-rose-500/20' : 'bg-gradient-to-r from-rose-50 to-rose-100/50 border border-rose-200'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-rose-400 to-rose-500 shadow-lg shadow-rose-500/50" />
                            <span className={`text-xs font-medium ${darkMode ? 'text-rose-300' : 'text-rose-700'}`}>Total Costs</span>
                          </div>
                          <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{formatMoney(totalCosts)}</span>
                        </div>
                        <div className={`h-1.5 rounded-full ${darkMode ? 'bg-slate-700/50' : 'bg-rose-100'}`}>
                          <div className="h-full rounded-full bg-gradient-to-r from-rose-400 to-rose-500" style={{ width: `${costsPct}%` }} />
                        </div>
                        <p className={`text-xs mt-1 text-right ${darkMode ? 'text-rose-400/60' : 'text-rose-600/60'}`}>{costsPct.toFixed(1)}%</p>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Cost Analysis */}
          {activeSection === 'projections' && (
            <div className="grid grid-cols-2 gap-8">
              <div className={`rounded-3xl p-8 ${darkMode ? 'bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-blue-500/10' : 'bg-white border border-slate-200 shadow-xl'} backdrop-blur-xl`}>
                <h3 className={`text-lg font-bold mb-6 ${darkMode ? 'text-white' : 'text-slate-800'}`}>Cost Analysis</h3>
                <div className="space-y-5">
                  <LuxuryInput label="Management Fees" value={inputs.managementFees} onChange={(v) => handleChange('managementFees', v)} suffix="%" dark={darkMode} />
                  <LuxuryInput label="Tax Rate" value={inputs.taxes} onChange={(v) => handleChange('taxes', v)} suffix="%" dark={darkMode} />
                  <div className="pt-1">
                    <label className={`block text-xs font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-blue-300/70' : 'text-slate-500'}`}>Tax Method</label>
                    <div className="grid grid-cols-2 gap-2">
                      {['net', 'gross'].map((method) => (
                        <button key={method} onClick={() => setTaxMethod(method)}
                          className={`py-2 rounded-xl text-sm font-semibold transition-all duration-300 ${taxMethod === method ? (darkMode ? 'bg-gradient-to-r from-amber-500 to-amber-400 text-slate-900 shadow-lg shadow-amber-500/30' : 'bg-gradient-to-r from-blue-500 to-blue-400 text-white shadow-lg shadow-blue-500/20') : (darkMode ? 'bg-slate-800/50 text-blue-300/70 hover:bg-slate-700/50 border border-blue-500/20' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200')}`}>
                          {method.charAt(0).toUpperCase() + method.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <LuxuryInput label="Term Insurance" value={inputs.termCosts} onChange={(v) => handleChange('termCosts', v)} prefix="$" dark={darkMode} />
                  <LuxuryInput label="Other Annual Cost" value={inputs.planningFees} onChange={(v) => handleChange('planningFees', v)} prefix="$" dark={darkMode} />
                  <LuxuryInput label="Opportunity Cost" value={inputs.opportunityCost} onChange={(v) => handleChange('opportunityCost', v)} suffix="%" dark={darkMode} />
                </div>
              </div>
              
              <div className={`rounded-3xl p-8 ${darkMode ? 'bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-blue-500/10' : 'bg-white border border-slate-200 shadow-xl'} backdrop-blur-xl`}>
                <h3 className={`text-lg font-bold mb-6 ${darkMode ? 'text-white' : 'text-slate-800'}`}>Cost Impact</h3>
                {lastYear && (
                  <div className="space-y-6">
                    <CostItem label={taxMethod === 'gross' ? "Cumulative Taxes + LOC" : "Cumulative Taxes"} value={lastYear.cumTax} dark={darkMode} />
                    <CostItem label="Cumulative Mgmt Fees" value={lastYear.cumMgmt} dark={darkMode} />
                    <CostItem label="Term + Opp Cost" value={lastYear.termTotal} dark={darkMode} />
                    <CostItem label="Other + Opp Cost" value={lastYear.planTotal} dark={darkMode} />
                    {parseFloat(inputs.inflation) > 0 && <CostItem label="Inflation Impact" value={lastYear.inflationCost} dark={darkMode} />}
                    <div className={`pt-5 border-t ${darkMode ? 'border-blue-500/10' : 'border-slate-200'}`}>
                      <div className="flex items-center justify-between">
                        <span className={`font-semibold ${darkMode ? 'text-blue-200/80' : 'text-slate-600'}`}>Total Costs</span>
                        <span className="text-3xl font-bold text-rose-400">{formatMoney(lastYear.cumTax + lastYear.cumMgmt + lastYear.termTotal + lastYear.planTotal + (parseFloat(inputs.inflation) > 0 ? lastYear.inflationCost : 0))}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* PLI Values */}
          {activeSection === 'insurance' && (
            <div className="space-y-8">
              <div className="flex justify-end">
                <button onClick={() => setPolicyModalOpen(true)} className={`px-5 py-2.5 rounded-xl font-semibold text-sm flex items-center gap-2 ${darkMode ? 'bg-gradient-to-r from-amber-500 to-amber-400 text-slate-900 hover:from-amber-400 hover:to-amber-300 shadow-lg shadow-amber-500/30' : 'bg-gradient-to-r from-blue-500 to-blue-400 text-white hover:from-blue-400 hover:to-blue-300 shadow-lg shadow-blue-500/20'} transition-all duration-300`}>
                  📋 Paste Policy Data
                </button>
              </div>
              <div className="grid grid-cols-3 gap-6">
                <div className={`rounded-3xl p-8 ${darkMode ? 'bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-blue-500/10' : 'bg-white border border-slate-200 shadow-xl'} backdrop-blur-xl`}>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>Policy Details</h3>
                    {policyData.baseCashValues.length > 0 && (
                      <span className={`text-xs px-3 py-1 rounded-full ${darkMode ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-blue-100 text-blue-600 border border-blue-200'}`}>
                        {policyData.baseCashValues.length} years
                      </span>
                    )}
                  </div>
                  {policyData.basePremium > 0 && (
                    <div className={`mb-5 p-4 rounded-xl ${darkMode ? 'bg-slate-900/50 border border-blue-500/10' : 'bg-slate-50 border border-slate-200'}`}>
                      <div className="flex items-center justify-between text-xs">
                        <span className={darkMode ? 'text-blue-300/60' : 'text-slate-500'}>Base Premium</span>
                        <span className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{formatMoney(policyData.basePremium)}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs mt-2">
                        <span className={darkMode ? 'text-blue-300/60' : 'text-slate-500'}>Scale Factor</span>
                        <span className={`font-semibold ${scalingFactor !== 1 ? (darkMode ? 'text-amber-400' : 'text-blue-600') : (darkMode ? 'text-white' : 'text-slate-800')}`}>
                          {(scalingFactor * 100).toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="space-y-5">
                    <div>
                      <LuxuryInput label="Annual Premium" value={inputs.annualPremium} onChange={(v) => handleChange('annualPremium', v)} prefix="$" dark={darkMode} />
                      {policyData.basePremium > 0 && (
                        <p className={`text-xs mt-2 ${darkMode ? 'text-blue-300/50' : 'text-slate-400'}`}>Adjust to scale all values proportionally</p>
                      )}
                    </div>
                    <LuxuryInput label={`Cash Value (Year ${inputs.timePeriod})`} value={inputs.futureCashValue} onChange={(v) => handleChange('futureCashValue', v)} prefix="$" dark={darkMode} />
                    <LuxuryInput label={`Death Benefit (Year ${inputs.timePeriod})`} value={inputs.futureDeathBenefit} onChange={(v) => handleChange('futureDeathBenefit', v)} prefix="$" dark={darkMode} />
                  </div>
                </div>
                
                <div className={`rounded-3xl p-8 text-center ${darkMode ? 'bg-gradient-to-br from-blue-900/30 to-blue-950/50 border border-blue-500/20' : 'bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 shadow-lg'} backdrop-blur-xl`}>
                  <div className={`w-16 h-16 mx-auto mb-5 rounded-2xl ${darkMode ? 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg shadow-blue-500/40' : 'bg-gradient-to-br from-blue-400 to-blue-500 shadow-lg shadow-blue-500/30'} flex items-center justify-center`}>
                    <span className="text-3xl text-white">◈</span>
                  </div>
                  <p className={`text-sm font-semibold uppercase tracking-wider mb-3 ${darkMode ? 'text-blue-400/70' : 'text-blue-600/70'}`}>Cash Value IRR</p>
                  <p className={`text-5xl font-bold ${pli.cvIRR >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>{pli.cvIRR.toFixed(2)}%</p>
                  <p className={`text-sm mt-3 ${darkMode ? 'text-blue-300/50' : 'text-blue-600/50'}`}>Year {inputs.timePeriod}</p>
                </div>
                
                <div className={`rounded-3xl p-8 text-center ${darkMode ? 'bg-gradient-to-br from-amber-900/20 to-amber-950/30 border border-amber-500/20' : 'bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 shadow-lg'} backdrop-blur-xl`}>
                  <div className={`w-16 h-16 mx-auto mb-5 rounded-2xl ${darkMode ? 'bg-gradient-to-br from-amber-500 to-amber-600 shadow-lg shadow-amber-500/40' : 'bg-gradient-to-br from-amber-400 to-amber-500 shadow-lg shadow-amber-500/30'} flex items-center justify-center`}>
                    <span className={`text-3xl ${darkMode ? 'text-slate-900' : 'text-white'}`}>◆</span>
                  </div>
                  <p className={`text-sm font-semibold uppercase tracking-wider mb-3 ${darkMode ? 'text-amber-400/70' : 'text-amber-600/70'}`}>Death Benefit IRR</p>
                  <p className={`text-5xl font-bold ${pli.dbIRR >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>{pli.dbIRR.toFixed(2)}%</p>
                  <p className={`text-sm mt-3 ${darkMode ? 'text-amber-300/50' : 'text-amber-600/50'}`}>Year {inputs.timePeriod}</p>
                </div>
              </div>
            </div>
          )}

          {/* Compare */}
          {activeSection === 'compare' && <CompareSection />}

          {/* Projections Table */}
          {activeSection === 'details' && (
            <div className={`rounded-3xl overflow-hidden ${darkMode ? 'bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-blue-500/10' : 'bg-white border border-slate-200 shadow-xl'} backdrop-blur-xl`}>
              <div className={`${darkMode ? 'bg-gradient-to-r from-blue-600 to-blue-500' : 'bg-gradient-to-r from-blue-500 to-blue-400'} p-5`}>
                <h3 className="text-lg font-bold text-white">Year-by-Year Projections</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className={darkMode ? 'bg-slate-900/50' : 'bg-slate-50'}>
                      {['Year', 'Contribution', 'Cum. Contrib', 'Gross Asset', 'Year Gain', 'Year Tax', 'Cum. Tax', 'Mgmt Fee', 'Cum. Mgmt', 'Term Total', 'Other Total', 'Inflation', 'Account Val', 'Net Value'].map((h, i) => (
                        <th key={h} className={`px-3 py-3 ${i === 0 ? 'text-left' : 'text-right'} font-semibold ${[3, 13].includes(i) ? (darkMode ? 'text-amber-400' : 'text-blue-600') : [6, 8, 9, 10, 11].includes(i) ? 'text-rose-400' : (darkMode ? 'text-blue-300/70' : 'text-slate-500')}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {yearlyData.map((row, i) => (
                      <tr key={row.year} className={`${i % 2 === 0 ? (darkMode ? 'bg-slate-800/20' : 'bg-slate-50/50') : ''} ${darkMode ? 'hover:bg-blue-500/5' : 'hover:bg-blue-50'} transition-colors`}>
                        <td className={`px-3 py-2.5 font-medium ${darkMode ? 'text-white' : 'text-slate-800'}`}>{row.year}</td>
                        <td className={`px-3 py-2.5 text-right ${darkMode ? 'text-blue-200/70' : 'text-slate-600'}`}>{formatMoney(row.contribution)}</td>
                        <td className={`px-3 py-2.5 text-right ${darkMode ? 'text-blue-200/70' : 'text-slate-600'}`}>{formatMoney(row.cumContributions)}</td>
                        <td className={`px-3 py-2.5 text-right font-medium ${darkMode ? 'text-amber-400' : 'text-blue-600'}`}>{formatMoney(row.grossAssetValue)}</td>
                        <td className="px-3 py-2.5 text-right text-emerald-400">{formatMoney(row.yearGain)}</td>
                        <td className={`px-3 py-2.5 text-right ${darkMode ? 'text-blue-200/70' : 'text-slate-600'}`}>{formatMoney(row.yearTax)}</td>
                        <td className="px-3 py-2.5 text-right text-rose-400">{formatMoney(row.cumTax)}</td>
                        <td className={`px-3 py-2.5 text-right ${darkMode ? 'text-blue-200/70' : 'text-slate-600'}`}>{formatMoney(row.mgmtFee)}</td>
                        <td className="px-3 py-2.5 text-right text-rose-400">{formatMoney(row.cumMgmt)}</td>
                        <td className="px-3 py-2.5 text-right text-rose-400">{formatMoney(row.termTotal)}</td>
                        <td className="px-3 py-2.5 text-right text-rose-400">{formatMoney(row.planTotal)}</td>
                        <td className="px-3 py-2.5 text-right text-rose-400">{formatMoney(row.inflationCost)}</td>
                        <td className={`px-3 py-2.5 text-right ${darkMode ? 'text-blue-200/70' : 'text-slate-600'}`}>{formatMoney(row.accountValue)}</td>
                        <td className="px-3 py-2.5 text-right font-medium text-blue-400">{formatMoney(row.netValue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}